/* GM Event Manager — EventData.cabal_ems_* (backend app/admin/events.py, GM4).
   Distinto do eventsService.ts (eventos públicos do site). */
import { apiGet, apiPost, apiPut, apiDelete } from './api';

/* ---------------------------------------------- tipos (camelCase = CamelModel) */
export interface EventSummary {
  eventId: number;
  eventType: number;
  name: string;
  beginDate?: string | null;
  endDate?: string | null;
  lastModifiedDate?: string | null;
  useFlag: number;
  worldIndex: number;
  npcIndex: number;
  participates: number;
  serverIndex: string;
  resetDay: number;
  resetHour: number;
  resetMinute: number;
  logMessages?: string | null;
  status: 'active' | 'inactive' | 'scheduled' | 'expired';
  editorSupported: boolean;
}

export interface EventCreateInput {
  eventType: number;
  name: string;
  beginDate: string;
  endDate: string;
  serverIndex?: string;
  worldIndex?: number;
  npcIndex?: number;
  participates?: number;
  resetDay?: number;
  resetHour?: number;
  resetMinute?: number;
  logMessages?: string | null;
}
export type EventUpdateInput = Partial<Omit<EventCreateInput, 'eventType'>>;

export interface GameItem { itemId: number; name: string }

/* Type 2 — Item Collection / Shop */
export interface PriceItem { itemKindIdx: number; itemOption: string; itemCount: number }
export interface ShopSlot {
  slotIdx: number;
  itemKindIdx: number;
  itemOption: string;
  itemDurationIdx: number;
  priceAlz: string;
  pricePoint: number;
  itemPriceId?: number | null;
  expirationTime?: string | null;
  dailyLimit: number;
  weeklyLimit: number;
  requiredItems: PriceItem[];
  priceGroupRefCount: number;
}
export interface NpcScript {
  worldIdx: number; npcIdx: number;
  script1?: string | null; script2?: string | null; script3?: string | null;
  script4?: string | null; script5?: string | null; scriptDesc?: string | null;
}
export interface ShopResponse {
  event: EventSummary;
  shopName?: string | null;
  uiStyle: number;
  npcScript?: NpcScript | null;
  slots: ShopSlot[];
}

/* Type 14 — Monster Drop */
export interface DropPoolItem { order: number; itemKindIdx: number; itemOption: string; itemDurationIdx: number; dropRate: number }
export interface DropTarget {
  dungeonIndex: number; mobSpeciesIdx: number;
  authorityType: number; perfectDrop: number; multiDropType: number;
  multiDropMin: number; multiDropMax: number; dropPoolId: number; spawnChannels: string;
}
export interface DropLimit {
  dungeonIndex: number; entryCountLimit: number;
  entryItemKind: string; entryItemOption: string; entryForcegem: number;
  limitItemKind: string; limitItemOption: string; dropCountLimit: number;
}
export interface MonsterDropResponse {
  event: EventSummary;
  targets: DropTarget[];
  pools: Record<string, DropPoolItem[]>;
  poolRefCounts: Record<string, number>;
  poolNames: Record<string, string>;
  limits: DropLimit[];
}

/* Type 11 — Monster Attack */
export interface AttackRound {
  orderId: number; noticeStartHour: number; noticeStartMinute: number;
  noticeTime: number; attackTime: number; spawnMonsterIndex: number;
  authorityType: number; perfectDrop: number; multiDropType: number;
  multiDropMin: number; multiDropMax: number; dropPoolId: number;
}
export interface AttackPoolItem { order: number; itemKind: number; itemOption: string; itemPeriod: number; ratio: number }
export interface MonsterAttackResponse {
  event: EventSummary;
  rounds: AttackRound[];
  worlds: number[];
  pools: Record<string, AttackPoolItem[]>;
  poolNames: Record<string, string>;
}

/* ---------------------------------------------- CRUD */
export const listEvents = (p?: { search?: string; type?: number; status?: string }) =>
  apiGet<EventSummary[]>('/admin/events', p as any);
export const getEvent = (id: number) => apiGet<EventSummary>(`/admin/events/${id}`);
export const createEvent = (b: EventCreateInput) => apiPost<EventSummary>('/admin/events', b);
export const updateEvent = (id: number, b: EventUpdateInput) => apiPut<EventSummary>(`/admin/events/${id}`, b);
export const toggleEvent = (id: number, enabled: boolean) => apiPost<EventSummary>(`/admin/events/${id}/toggle`, { enabled });
export const duplicateEvent = (id: number) => apiPost<EventSummary>(`/admin/events/${id}/duplicate`, {});
export const deleteEvent = (id: number) => apiDelete(`/admin/events/${id}`);
export const reloadEvents = () => apiPost('/admin/events/reload', {});
export const searchGameItems = (search: string) => apiGet<GameItem[]>('/admin/events/game-items', { search, limit: 40 });
export interface ResolvedItem { cleanKind: number; name?: string | null }
export const resolveItems = (kinds: number[]) => apiPost<Record<string, ResolvedItem>>('/admin/events/resolve-items', { kinds });

/* Type 2 */
export const getShop = (id: number) => apiGet<ShopResponse>(`/admin/events/${id}/shop`);
export const saveShopSlot = (id: number, slot: number, b: ShopSlot) => apiPut<ShopSlot>(`/admin/events/${id}/shop/slots/${slot}`, b);
export const deleteShopSlot = (id: number, slot: number) => apiDelete(`/admin/events/${id}/shop/slots/${slot}`);
export const saveShopMeta = (id: number, b: { shopName?: string; uiStyle?: number }) => apiPut(`/admin/events/${id}/shop/meta`, b);
export const saveNpcScript = (id: number, b: NpcScript) => apiPut(`/admin/events/${id}/npc-script`, b);

/* Type 14 */
export const getMonsterDrop = (id: number) => apiGet<MonsterDropResponse>(`/admin/events/${id}/monster-drop`);
export const addDropTarget = (id: number, b: DropTarget) => apiPost(`/admin/events/${id}/monster-drop/targets`, b);
export const updateDropTarget = (id: number, d: number, m: number, b: DropTarget) => apiPut(`/admin/events/${id}/monster-drop/targets/${d}/${m}`, b);
export const deleteDropTarget = (id: number, d: number, m: number) => apiDelete(`/admin/events/${id}/monster-drop/targets/${d}/${m}`);
export const editDropPool = (id: number, poolId: number, b: { items: DropPoolItem[]; name?: string; cloneIfShared?: boolean }) =>
  apiPut<{ ok: boolean; poolId: number; cloned: boolean }>(`/admin/events/${id}/monster-drop/pools/${poolId}`, b);
export const createDropPool = (id: number, b: { items: DropPoolItem[]; name?: string }) =>
  apiPost<{ ok: boolean; poolId: number }>(`/admin/events/${id}/monster-drop/pools`, b);
export const saveDropLimit = (id: number, d: number, b: DropLimit) => apiPut(`/admin/events/${id}/monster-drop/limits/${d}`, b);
export const deleteDropLimit = (id: number, d: number) => apiDelete(`/admin/events/${id}/monster-drop/limits/${d}`);

/* Type 11 */
export const getMonsterAttack = (id: number) => apiGet<MonsterAttackResponse>(`/admin/events/${id}/monster-attack`);
export const saveAttackRound = (id: number, orderId: number, b: AttackRound) => apiPut(`/admin/events/${id}/monster-attack/rounds/${orderId}`, b);
export const deleteAttackRound = (id: number, orderId: number) => apiDelete(`/admin/events/${id}/monster-attack/rounds/${orderId}`);
export const saveAttackWorlds = (id: number, worlds: number[]) => apiPut(`/admin/events/${id}/monster-attack/worlds`, { worlds });
export const saveAttackPool = (id: number, poolId: number, b: { poolId: number; name?: string; items: AttackPoolItem[] }) =>
  apiPut(`/admin/events/${id}/monster-attack/pools/${poolId}`, b);
export const deleteAttackPool = (id: number, poolId: number) => apiDelete(`/admin/events/${id}/monster-attack/pools/${poolId}`);
